import { z } from 'zod'

/**
 * The Library's URL state (spec §6.5).
 *
 * Validated by the router with the SAME kind of schema the API takes, which is
 * the reason TanStack Router was chosen over React Router: a filtered library
 * is shareable, survives a refresh and respects the back button, and none of
 * that is true if the state lives in `useState`.
 *
 * `.catch()` on every member is deliberate. Search params are user-editable —
 * somebody will paste a truncated URL — and a filtered list should fall back to
 * unfiltered rather than throwing a route error at them.
 */
export const resourcesSearchSchema = z.object({
  /*
   * `all` is the default, and that is the design's shape rather than a
   * preference.
   *
   * `resources.jsx:253` branches on whether anything is being filtered: with
   * nothing set it renders BROWSE — per-format sections, the resume card,
   * recommendations, collections — and only a query or a chosen type collapses
   * it to a flat grid. Defaulting to `courses` skipped the browse view
   * entirely, so the library opened on a filtered result nobody had asked for.
   */
  type: z
    .enum(['all', 'courses', 'readings', 'printables', 'podcasts'])
    .catch('all')
    .default('all'),
  q: z.string().max(200).optional().catch(undefined),
  destination: z.string().uuid().optional().catch(undefined),
  age: z.string().uuid().optional().catch(undefined),
  page: z.coerce.number().int().positive().catch(1).default(1),
})

export type ResourcesSearch = z.infer<typeof resourcesSearchSchema>
